import { Transition } from '@headlessui/react'
import { usePathname } from 'next/navigation'
import { FC, useEffect, useRef } from 'react'

import { useSelectedProject } from '@/api/models/main/hooks/useSelectedProject'
import { ScreenSize } from '@/app/constants'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import { useSelectedProjectStore } from '@/components/entities/common/model/selected-project.store'
import {
  SidebarContacts,
  SidebarHead,
  SidebarNav,
  SidebarProjectSelector
} from '@/components/widgets/sidebar/ui'

import styles from './Sidebar.module.css'

type Props = {
  isSidebarExpanded: boolean
  expandSidebar: () => void
  collapseSidebar: () => void
  toggleSidebar: () => void
}

const Sidebar: FC<Props> = ({
  isSidebarExpanded,
  expandSidebar,
  collapseSidebar,
  toggleSidebar
}) => {
  const currentRoute = usePathname()
  const { breakpoint } = useCurrentBreakpoint()
  const isSmallScreen = [ScreenSize.Xxs, ScreenSize.Xs].includes(breakpoint)

  const { setModalOpened } = useSelectedProjectStore()
  const { selectedProject } = useSelectedProject()
  const isDesktopScreen = [ScreenSize.Xxl].includes(breakpoint)

  const hasAutoExpandedOnDesktop = useRef(false)
  const previousRoute = useRef(currentRoute)

  useEffect(() => {
    if (isDesktopScreen && !hasAutoExpandedOnDesktop.current) {
      expandSidebar()
      hasAutoExpandedOnDesktop.current = true
    }
  }, [expandSidebar, isDesktopScreen])

  useEffect(() => {
    const hasRouteChanged = previousRoute.current !== currentRoute

    if (hasRouteChanged && isSmallScreen) {
      collapseSidebar()
    }

    previousRoute.current = currentRoute
  }, [collapseSidebar, currentRoute, isSmallScreen])

  const onProjectSelect = () => {
    setModalOpened(true)
    collapseSidebar()
  }

  return (
    <>
      <Transition
        show={isSidebarExpanded}
        enter="duration-300 z-50"
        enterFrom="opacity-0"
        leave="duration-300"
        leaveTo="opacity-0 z-50"
      >
        <button
          className="fixed inset-0 z-50 hidden bg-[#000]/50 xl:block xs:hidden"
          onClick={collapseSidebar}
        />
      </Transition>

      <div className="relative z-50 flex flex-col">
        <div
          className={cn(
            'fixed bottom-0 top-0 flex w-[84px] flex-col transition-all',
            'overflow-hidden rounded-r-20 bg-grey-100',
            isSidebarExpanded && 'w-[304px]',
            'xs:-left-full xs:right-auto xs:w-full xs:overflow-y-scroll',
            isSidebarExpanded &&
              'xs:left-0 xs:right-0 xs:top-0 xs:w-auto xs:rounded-0',
            styles.scroll
          )}
        >
          <SidebarHead
            className="shrink-0 xs:sticky xs:top-0 xs:z-10"
            isSidebarExpanded={isSidebarExpanded}
            toggleSidebar={toggleSidebar}
            collapseSidebar={collapseSidebar}
          />

          {isSmallScreen && selectedProject && (
            <SidebarProjectSelector
              onClick={onProjectSelect}
              selectedProjectName={selectedProject?.bc_name}
            />
          )}

          <SidebarNav
            className={cn('grow', isSidebarExpanded && 'min-w-[304px]')}
            isSidebarExpanded={isSidebarExpanded}
            toggleSidebar={toggleSidebar}
          />

          <SidebarContacts
            className={cn('shrink-0', isSidebarExpanded && 'min-w-[304px]')}
            isSidebarExpanded={isSidebarExpanded}
          />
        </div>
      </div>
    </>
  )
}

export { Sidebar }
